import dayjs from "dayjs";
import { useCallback } from "react";
import { PostTaskCompleteParams } from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { Coords, VerifierQrPayloadWithScanMeta } from "tm-shared/types";
import { createUniqueId } from "tm-shared/utils";

import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { makeCommand } from "@/hooks/requestQueue/useRequestQueue";
import { clearTaskPayload } from "@/store/slices/progress";
import { removeSupervisorCheck } from "@/store/slices/work";
import { AnyQueueCommand } from "@/types/requestQueue";
import { getPreciseLocation } from "@/utils/geo/geo";

import { isManagerRole } from "../../../utils/business/isManagerRole";

export interface TaskCompleteData {
    coords: Coords | null;
    isoDate: string;
    backendDate: string;
}

const prepareTaskCompleteData = async (
    inputCoords?: null | Coords,
): Promise<TaskCompleteData> => {
    let coords = inputCoords;

    if (coords === undefined) {
        try {
            coords = await getPreciseLocation();
        } catch {
            coords = null;
        }
    }

    const isoDate = dayjs().toISOString();
    const backendDate = dayjs(isoDate).format(backendDateFormat.dateTime);

    return { coords, isoDate, backendDate };
};

const prepareVisitVerifyData = (
    data: VerifierQrPayloadWithScanMeta,
): { visitVerify: PostTaskCompleteParams["visitVerify"] } => {
    const { id, generatedAt, geo, distance, managerId, scannedAt } = data;

    return {
        visitVerify: {
            id,
            generatedAt,
            distance,
            userId: managerId,
            scannedAt,
            geo: {
                latitude: geo.lat,
                longitude: geo.lng,
                accuracy: geo.accuracy,
            },
        },
    };
};

interface PrepareManagerVisitDataParams {
    userId: string;
    coords: Coords | null;
    backendDate: string;
}

const prepareManagerVisitVerifyData = ({
    userId,
    coords,
    backendDate,
}: PrepareManagerVisitDataParams): {
    visitVerify: PostTaskCompleteParams["visitVerify"];
} => {
    return {
        visitVerify: {
            id: createUniqueId(),
            generatedAt: backendDate,
            distance: 0,
            userId,
            scannedAt: backendDate,
            geo: {
                latitude: coords?.lat ?? 0,
                longitude: coords?.lng ?? 0,
                accuracy: coords?.accuracy ?? 0,
            },
        },
    };
};

interface MakeTaskCompleteCommandArgs {
    params: Omit<
        PostTaskCompleteParams,
        "infoContext" | "completedAt" | "performer"
    >;
    coords?: Coords | null;
}

export const useMakeCommand = () => {
    const dispatch = useAppDispatch();

    const supervisorCheck = useAppSelector((s) => s.work.supervisorCheck);
    const role = useProtectedUserRoleSelector();
    const userId = useAppSelector((s) => s.user.id);
    const activeSchedule = useAppSelector((s) => s.manager.activeSchedule);

    const makeTaskCompleteCommands = useCallback(
        async ({
            params,
            coords: inputCoords,
        }: MakeTaskCompleteCommandArgs): Promise<AnyQueueCommand[]> => {
            const { isoDate, backendDate, coords } =
                await prepareTaskCompleteData(inputCoords);

            const commands: AnyQueueCommand[] = [];

            if (coords) {
                commands.push(
                    makeCommand({
                        type: "postGeoAdd",
                        title: "Надсилання геоданих",
                        params: {
                            accuracy: coords.accuracy,
                            latitude: coords.lat,
                            longitude: coords.lng,
                            locationTime: backendDate,
                        },
                        options: {
                            stopOnError: false,
                        },
                    }),
                );
            }

            const { taskId } = params;

            const currentCheck = supervisorCheck[taskId];
            const isManager = isManagerRole(role);

            const visitVerifyData = currentCheck
                ? prepareVisitVerifyData(currentCheck)
                : isManager
                  ? prepareManagerVisitVerifyData({
                        userId,
                        backendDate,
                        coords,
                    })
                  : undefined;

            const performer = isManager ? activeSchedule?.userId : userId;

            if (!performer) throw new Error("performer is required");

            commands.push(
                makeCommand({
                    type: "postTaskComplete",
                    title: "Завершення задачі",
                    options: {
                        stopOnError: true,
                        ignoreErrorCodes: ["-32015"],
                    },
                    params: {
                        ...params,
                        infoContext: {
                            geo: coords,
                            completedAt: isoDate,
                        },
                        performer,
                        completedAt: backendDate,
                        ...visitVerifyData,
                    },
                }),
            );

            if (currentCheck) {
                dispatch(removeSupervisorCheck(taskId));
            }

            dispatch(clearTaskPayload({ taskId }));

            return commands;
        },
        [activeSchedule?.userId, dispatch, role, supervisorCheck, userId],
    );

    const makeUploadPhotoCommands = useCallback((photoIds: string[]) => {
        return photoIds.map((photoId) =>
            makeCommand({
                type: "postFileUpload",
                title: "photo",
                params: photoId,
                options: {
                    stopOnError: false,
                },
            }),
        );
    }, []);

    return { makeTaskCompleteCommands, makeUploadPhotoCommands };
};
